import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import MapIcon from '@mui/icons-material/Map';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import useObjectState from 'src/hooks/useObjectState';
import { useCodeSystem, useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { GridItem, GridSection } from 'src/components/CustomModal';
import {
  getCoordinatesForMap,
  getFullAddress,
  getLocationAddressString,
  getServiceAddressStringForGeocode,
} from 'src/careflow/common/common-utils';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ReferralService, ServiceByFiltersResponse } from 'src/crs/referral/services/ReferralService';
import { Address } from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { capitalize } from 'src/utils/string';
import { internalExternalOrg } from './constants';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { fetchOrganizationAffiliation } from 'src/careflow/helpers/common';
import { getReferenceId } from 'src/utils/fhir';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import uuidv4 from 'src/utils/uuidv4';

type Props = {
  patient?: WrappedPatient | null;
  updateState: any;
  openSearch: boolean;
};

const DEFAULT_MAP_CENTER = { lat: 38.8966, lng: -99.5259 };
const DEFAULT_ZOOM = 10;
const MAP_CONTAINER_STYLE = { width: '100%', height: 400, margin: 0, padding: 0 };
const BOUNDS_PADDING_PX = 48;
const MIN_ZOOM = 5;
const MAX_ZOOM = 14;
const MAX_BOUNDS_SPAN_DEG = 15;
const SAME_ADDRESS_MARKER_OFFSET_DEG = 0.008;

function isValidCoord(p: { lat: number; lng: number }): boolean {
  const { lat, lng } = p;
  if (lat === 0 && lng === 0) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function positionsForBounds(
  patientMarkers: { lat: number; lng: number }[],
  allMarkers: { coords: { lat: number; lng: number }; listIndex: number }[]
): { lat: number; lng: number }[] {
  const validPatient = patientMarkers.filter(isValidCoord);
  const halfSpan = MAX_BOUNDS_SPAN_DEG / 2;
  const anchor = validPatient.length > 0 ? validPatient[0] : null;
  const withinRange = anchor
    ? (p: { lat: number; lng: number }) =>
        Math.abs(p.lat - anchor.lat) <= halfSpan &&
        Math.abs(p.lng - anchor.lng) <= halfSpan
    : () => true;

  const positions: { lat: number; lng: number }[] = anchor
    ? validPatient.filter(withinRange)
    : [...validPatient];
  allMarkers.forEach((item) => {
    const { coords } = item;
    const atSameSpot = allMarkers.filter(
      (m) =>
        Math.abs(m.coords.lat - coords.lat) < 1e-6 &&
        Math.abs(m.coords.lng - coords.lng) < 1e-6
    );
    const idxAtSpot = atSameSpot.indexOf(item);
    const nudge =
      atSameSpot.length > 1
        ? {
            lat: (idxAtSpot % 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
            lng: Math.floor(idxAtSpot / 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
          }
        : { lat: 0, lng: 0 };
    const p = { lat: coords.lat + nudge.lat, lng: coords.lng + nudge.lng };
    if (isValidCoord(p) && withinRange(p)) positions.push(p);
  });
  if (positions.length === 0 && validPatient.length > 0) return validPatient;
  return positions;
}

type SearchServicesMapViewProps = {
  apiKey: string;
  mapCenter: { lat: number; lng: number };
  patientAddressMarkers: { lat: number; lng: number }[];
  patientName?: string;
  services: ServiceByFiltersResponse[];
  onSelectService: (service: ServiceByFiltersResponse) => void;
};

function renderResourceBlock(
  title: string,
  row: { name?: string; comment?: string; address?: any; telecom?: { value?: string }[] } | null | undefined,
  getFullAddress: (address: any) => string
) {
  if (!row) return null;
  const name = row?.name;
  const comment = row?.comment;
  const address = row?.address ? getFullAddress(row.address) : null;
  const telecom = row?.telecom?.map((t: any) => t?.value).filter(Boolean) ?? [];
  if (!name && !comment && !address && telecom.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: '#333' }}>{title}</div>
      {name && <div style={{ fontSize: 13 }}>{name}</div>}
      {comment && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{comment}</div>}
      {address && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{address}</div>}
      {telecom.length > 0 && (
        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
          {telecom.join(' · ')}
        </div>
      )}
    </div>
  );
}

function ServiceMarkerDetailContent({
  serviceRow,
  onSelect,
  getFullAddress,
}: {
  serviceRow: ServiceByFiltersResponse;
  onSelect: () => void;
  getFullAddress: (address: any) => string;
}) {
  const hs = serviceRow?.healthcareService;
  const org = serviceRow?.organization;
  const loc = serviceRow?.location;
  return (
    <div style={{ padding: 4, maxWidth: 320, minWidth: 220 }}>
      {renderResourceBlock('Service', hs, getFullAddress)}
      {renderResourceBlock('Organization', org, getFullAddress)}
      {renderResourceBlock('Location', loc, getFullAddress)}
      <button
        type="button"
        onClick={onSelect}
        style={{
          marginTop: 8,
          padding: '6px 14px',
          backgroundColor: '#2e7d32',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        Select
      </button>
    </div>
  );
}

function serviceMapKey(s: ServiceByFiltersResponse): string {
  return `${s?.healthcareService?.id ?? ''}-${s?.location?.id ?? ''}`;
}

function SearchServicesMapView({
  apiKey,
  mapCenter,
  patientAddressMarkers,
  patientName,
  services,
  onSelectService,
}: SearchServicesMapViewProps) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: ['places'],
  });

  const [resolvedCoords, setResolvedCoords] = useState<Record<string, { lat: number; lng: number }>>({});
  const [geocodeAttempted, setGeocodeAttempted] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedService, setSelectedService] = useState<{
    serviceRow: ServiceByFiltersResponse;
    position: { lat: number; lng: number };
  } | null>(null);

  const servicesWithCoords = useMemo(
    () =>
      (services ?? [])
        .map((s, listIndex) => ({ service: s, coords: getCoordinatesForMap(s?.location), listIndex }))
        .filter((x): x is { service: ServiceByFiltersResponse; coords: { lat: number; lng: number }; listIndex: number } =>
          x.coords !== null
        ),
    [services]
  );

  const servicesNeedingGeocode = useMemo(
    () =>
      (services ?? []).filter((s) => {
        if (getCoordinatesForMap(s?.location) !== null) return false;
        return getServiceAddressStringForGeocode(s).length > 0;
      }),
    [services]
  );

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.Geocoder || servicesNeedingGeocode.length === 0) {
      setGeocodeAttempted(servicesNeedingGeocode.length > 0);
      return;
    }
    setGeocodeAttempted(true);
    const geocoder = new window.google.maps.Geocoder();
    let cancelled = false;
    const timer = window.setTimeout(() => {
      servicesNeedingGeocode.forEach((s) => {
        const key = serviceMapKey(s);
        const address = getServiceAddressStringForGeocode(s);
        if (!address) return;
        geocoder.geocode({ address }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (cancelled) return;
          if (status !== window.google.maps.GeocoderStatus.OK || !results?.[0]) return;
          const loc = results[0].geometry?.location;
          if (!loc) return;
          setResolvedCoords((prev) => ({ ...prev, [key]: { lat: loc.lat(), lng: loc.lng() } }));
        });
      });
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isLoaded, servicesNeedingGeocode]);

  useEffect(() => {
    setResolvedCoords((prev) => {
      const keys = new Set(servicesNeedingGeocode.map(serviceMapKey));
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((k) => {
        if (!keys.has(k)) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [servicesNeedingGeocode]);

  const serviceList = useMemo(() => services ?? [], [services]);

  const servicesWithResolvedCoords = useMemo(() => {
    return servicesNeedingGeocode
      .map((s) => {
        const listIndex = serviceList.findIndex((sv) => serviceMapKey(sv) === serviceMapKey(s));
        return {
          service: s,
          coords: resolvedCoords[serviceMapKey(s)] as { lat: number; lng: number } | undefined,
          listIndex: listIndex >= 0 ? listIndex : serviceList.length,
        };
      })
      .filter((x): x is { service: ServiceByFiltersResponse; coords: { lat: number; lng: number }; listIndex: number } => x.coords != null);
  }, [servicesNeedingGeocode, resolvedCoords, serviceList]);

  const allServiceMarkers = useMemo(() => {
    const combined = [...servicesWithCoords, ...servicesWithResolvedCoords];
    return [...combined].sort((a, b) => a.listIndex - b.listIndex);
  }, [servicesWithCoords, servicesWithResolvedCoords]);

  const allMarkerPositions = useMemo(() => {
    const positions: { lat: number; lng: number }[] = [...patientAddressMarkers];
    allServiceMarkers.forEach((item) => {
      const { coords } = item;
      const atSameSpot = allServiceMarkers.filter(
        (m) =>
          Math.abs(m.coords.lat - coords.lat) < 1e-6 &&
          Math.abs(m.coords.lng - coords.lng) < 1e-6
      );
      const idxAtSpot = atSameSpot.indexOf(item);
        const nudge =
          atSameSpot.length > 1
            ? {
                lat: (idxAtSpot % 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
                lng: Math.floor(idxAtSpot / 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
              }
            : { lat: 0, lng: 0 };
      positions.push({
        lat: coords.lat + nudge.lat,
        lng: coords.lng + nudge.lng,
      });
    });
    return positions;
  }, [patientAddressMarkers, allServiceMarkers]);

  const boundsPositions = useMemo(
    () => positionsForBounds(patientAddressMarkers, allServiceMarkers),
    [patientAddressMarkers, allServiceMarkers]
  );

  useEffect(() => {
    if (!mapReady || !mapRef.current || boundsPositions.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    boundsPositions.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, BOUNDS_PADDING_PX);
  }, [mapReady, boundsPositions]);

  if (!isLoaded) {
    return (
      <Box
        sx={{
          width: '100%',
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading map...
        </Typography>
      </Box>
    );
  }

  const hasServicesButNoMarkers = (services?.length ?? 0) > 0 && allServiceMarkers.length === 0;
  const hadAddressesToGeocode = servicesNeedingGeocode.length > 0;

  return (
    <Box sx={{ width: '100%', mt: 0, pt: 0 }}>
    <GoogleMap
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={mapCenter}
      zoom={DEFAULT_ZOOM}
      options={{
        mapTypeControl: true,
        fullscreenControl: true,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
      }}
      onLoad={(map) => {
        mapRef.current = map;
        setMapReady(true);
      }}
      onUnmount={() => {
        mapRef.current = null;
        setMapReady(false);
      }}
    >
      {patientAddressMarkers.map((marker, i) => (
        <Marker
          key={`patient-addr-${i}`}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={patientName || 'Patient'}
          icon={{
            path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
            scale: 10,
            fillColor: '#1976d2',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          }}
        />
      ))}
      {allServiceMarkers.map((item, i) => {
        const { service: serviceRow, coords, listIndex } = item;
        const atSameSpot = allServiceMarkers.filter(
          (m) =>
            Math.abs(m.coords.lat - coords.lat) < 1e-6 &&
            Math.abs(m.coords.lng - coords.lng) < 1e-6
        );
        const idxAtSpot = atSameSpot.indexOf(item);
        const nudge =
          atSameSpot.length > 1
            ? {
                lat: (idxAtSpot % 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
                lng: Math.floor(idxAtSpot / 3) * SAME_ADDRESS_MARKER_OFFSET_DEG - SAME_ADDRESS_MARKER_OFFSET_DEG,
              }
            : { lat: 0, lng: 0 };
        const position = {
          lat: coords.lat + nudge.lat,
          lng: coords.lng + nudge.lng,
        };
        const letter = String.fromCharCode(65 + (i % 26));
        const label = [
          serviceRow?.healthcareService?.name,
          serviceRow?.organization?.name,
          serviceRow?.location?.name,
        ]
          .filter(Boolean)
          .join(' | ');
        return (
          <Marker
            key={`service-${serviceRow?.healthcareService?.id}-${serviceRow?.location?.id}-${i}`}
            position={position}
            title={label ? `${letter}: ${label}` : letter}
            icon={{
              url: `https://waqi.info/mapicon/${letter}.30.png`,
              anchor: new window.google.maps.Point(12, 12),
              scaledSize: new window.google.maps.Size(24, 24),
            }}
            clickable
            onClick={() => setSelectedService({ serviceRow, position })}
          />
        );
      })}
      {selectedService && (
        <InfoWindow
          position={selectedService.position}
          onCloseClick={() => setSelectedService(null)}
        >
          <ServiceMarkerDetailContent
            serviceRow={selectedService.serviceRow}
            onSelect={() => {
              onSelectService(selectedService.serviceRow);
              setSelectedService(null);
            }}
            getFullAddress={getFullAddress}
          />
        </InfoWindow>
      )}
    </GoogleMap>
    {hasServicesButNoMarkers && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 0.5 }}>
        {hadAddressesToGeocode && geocodeAttempted
          ? 'Could not geocode service addresses. Enable the Geocoding API for your Google Maps key in Google Cloud Console, or run the geocode script to add coordinates to FHIR Location resources.'
          : 'No coordinates for the listed services. Ensure each service has a location or organization with an address (or run the geocode script to add coordinates to Location resources).'}
      </Typography>
    )}
    </Box>
  );
}

const SearchServices = ({ patient, updateState, openSearch }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useLocales();
  const authUser = useAuth().getCurrentUser();
  const { configurations } = useTenantConfigData();
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [state, updateStateFilters] = useObjectState<any>({});
  const [services, setServices] = useState<any>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<WrappedOrganization | null>(
    null
  );
  const [organizationInternalExternal, setOrganizationInternalExternal] = useState<string[]>([]);
  const [disabledFilterByCoverageArea, setDisabledFilterByCoverageArea] = useState(false);
  const [coverageByArea, setCoverageByArea] = useState<any>([]);
  const [serviceName, setServiceName] = useState('');
  const [internalExternal, setInternalExternal] = useState<string[]>(['all']);

  const [serviceType] = useCodeSystem('ph-referral-services');
  const [organizations, { isFetching: isOrganizationsLoading }] = useOrganizations({
    map: OrganizationWrapper,
  });

  const userOrganizations = useMemo(
    () => authUser?.organizations ?? [],
    [authUser?.organizations]
  );
  
  const orgsKey = useMemo(
    () => JSON.stringify(userOrganizations),
    [userOrganizations]
  );

  const managingOrgRef = patient?.managingOrganization?.reference;

  useEffect(() => {
    const getOrgsInternalExternal = async () => {
      if (!internalExternal?.length || internalExternal.includes('all')) {
        setOrganizationInternalExternal([]);
        return
      };
  
      const checkedInternal = internalExternal.includes('internal');
      const checkedExternal = internalExternal.includes('external');
  
      let result: string[] = [];
      
      const patientManagingOrgId = getReferenceId(managingOrgRef);
      const baseOrganizations = patientManagingOrgId ? [patientManagingOrgId] : [orgsKey];
  
      if (checkedExternal) {
        const affiliations = await fetchOrganizationAffiliation(baseOrganizations);
        result = affiliations;
      }
  
      if (checkedInternal) {
        result = [...result, ...baseOrganizations ?? []];
      }

      setOrganizationInternalExternal(Array.from(new Set(result)));
    };
  
    getOrgsInternalExternal();
  }, [internalExternal, managingOrgRef, orgsKey]);

  const { serviceTypeItem, postalCode, city, states, serviceCoverageArea, isLoading } = state;

  const referralServiceTypes = useMemo(
    () =>
      serviceType?.concept
        ?.map((type) => ({
          label: type.display,
          value: type.code,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [serviceType]
  );

  const postalCodes = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.postalCode,
          value: address?.postalCode,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  const statesPatient = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.state,
          value: address?.state,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  const citiesPatient = useMemo(
    () =>
      patient?.address
        ?.map((address: Address) => ({
          label: address?.city,
          value: address?.city,
        }))
        .sort((x1, x2) =>
          x1.label && x2.label && x1.label > x2.label
            ? 1
            : x1.label && x2.label && x1.label < x2.label
            ? -1
            : 0
        ) ?? [],
    [patient]
  );

  const mapCenter = useMemo(() => {
    const primaryAddress = patient?.getPrimaryAddress?.();
    const coords = getCoordinatesForMap(primaryAddress);
    if (coords) return coords;
    return DEFAULT_MAP_CENTER;
  }, [patient]);

  const patientAddressMarkers = useMemo(() => {
    const addresses = patient?.address ?? [];
    return addresses
      .map((address: Address) => {
        const coords = getCoordinatesForMap(address);
        return coords ? { lat: coords.lat, lng: coords.lng } : null;
      })
      .filter(Boolean) as { lat: number; lng: number }[];
  }, [patient]);

  useEffect(() => {
    if (!openSearch) return;

    const getCoverageArea = async () => {
      if (orgsKey?.length > 0) {
        const locations = await ReferralService.getLocationFromOrganizationUsers(userOrganizations);
        const locationIds = locations?.map((e) => e?.id);

        if(locationIds && locationIds?.length > 0){
          setCoverageByArea(locationIds)
        }else {
          setDisabledFilterByCoverageArea(true);
        }
      }
    };
    getCoverageArea();
  }, [openSearch, serviceCoverageArea, orgsKey]);

  const handleSearchServices = async () => {
    setServices([]);

    const organizationIds = selectedOrganization 
      ? selectedOrganization?.id 
      : organizationInternalExternal?.length > 0 
      ? organizationInternalExternal 
      : null;
    
    const searchParams = {
      serviceType: serviceTypeItem?.value || serviceTypeItem,
      postalCode: postalCode?.value || postalCode,
      state: states?.value || states,
      organizationId: organizationIds,
      serviceName,
      city: city?.value || city,
      ...(serviceCoverageArea && { serviceCoverageArea: coverageByArea })
    };

    updateStateFilters({ isLoading: true });

    try {
      const servicesResponse = await ReferralService.getServicesByFilters(searchParams);
      if (servicesResponse) {
        setServices(servicesResponse);
      }
    } catch (error) {
      enqueueSnackbar(`An error has occurred.`, { variant: 'error' });
    }
    updateStateFilters({ isLoading: false });
  };

  const handleSelectedService = (data: ServiceByFiltersResponse) => {
    updateState({
      serviceTypeItem: {
        label: `${data?.healthcareService?.name}${
          data?.organization?.name ? ` | ${data?.organization?.name}` : ''
        } `,
        value: {
          healthcareService: data?.healthcareService,
          organization: data?.organization,
        },
      },
      locationOrganization: {
        label: data?.location?.name,
        value: {
          location: data?.location,
        },
      },
      openSearch: false,
    });
  };

  const columns: GridColDef[] = [
    { 
      field: 'healthcareService',
      headerName: 'Service',
      flex: 1,
      renderCell: (params) => setServiceRow(params?.row?.healthcareService)
    },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1,
      renderCell: (params) => setRow(params?.row?.organization)
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      renderCell: (params) => setRow(params?.row?.location)
    },
    {
      field: 'edit',
      headerName: '',
      flex: 0.3,
      sortable: false,
      renderCell: (params) => {
        const { healthcareService } = params.row;
        return !isEmpty(healthcareService) ? (
          <Button
            sx={{
              height: 36,
              width: 63,
            }}
            onClick={() => {
              handleSelectedService(params.row);
            }}
            variant="contained"
          >
            Select
          </Button>
        ) : null;
      },
    },
  ];

  return (
    <>
      <Grid item xs={12} mt={0}>
        <form>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.serviceName', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  value={serviceName}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const text = event.target.value;
                    setServiceName(text);
                  }}
                  variant="standard"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.serviceType', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={referralServiceTypes}
                  value={serviceTypeItem}
                  onChange={(_: any, value: any | null) => {
                    if (value && value?.value) {
                      updateStateFilters({ serviceTypeItem: value });
                    }
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ serviceTypeItem: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <FormControl fullWidth>
                <FormGroup row>
                  {internalExternalOrg.map(({ code, display }) => (
                    <FormControlLabel
                      key={code}
                      control={
                        <Checkbox
                          checked={internalExternal?.includes(code || '')}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setInternalExternal((prev) => {
                              const newCode = code || '';
                              if (checked) {
                                if (newCode === 'all') {
                                  return ['all'];
                                } else {
                                  const filtered = prev.filter(c => c !== 'all');
                                  return [...filtered, newCode];
                                }
                              } else {
                                return prev.filter(c => c !== newCode);
                              }
                            });
                          }}
                        />
                      }
                      label={i18n(`patients.details.serviceRequests.searchService.${code}`, 'crs')}
                      sx={{ py: 1, flexGrow: 1 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.organization', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  value={selectedOrganization}
                  fullWidth
                  onChange={(_, organization) => {
                    setSelectedOrganization(organization);
                  }}
                  options={
                    !internalExternal.includes('all') && (!organizationInternalExternal || organizationInternalExternal.length === 0)
                      ? []
                      : internalExternal.includes('all')
                      ? organizations ?? []
                      : organizations?.filter((e) =>
                          organizationInternalExternal?.some((s) => s === e?.id)
                        ) ?? []
                  }
                  getOptionLabel={(organization: WrappedOrganization) =>
                    capitalize(organization?.name ?? '')
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isOrganizationsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.postalCode', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={postalCodes}
                  value={postalCode}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ postalCode: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ postalCode: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.city', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={citiesPatient}
                  value={city}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ city: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ city: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
          </GridSection>
          <GridSection>
            <GridItem>
              <InputLabel shrink sx={{ mt: 2 }}>
                {i18n('patients.details.serviceRequests.searchService.state', 'crs')}
              </InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={statesPatient}
                  value={states}
                  onChange={(_: any, value: any | null) => {
                    updateStateFilters({ states: value });
                  }}
                  onInputChange={(_: any, value: any | null) => {
                    updateStateFilters({ states: value });
                  }}
                  renderInput={(params) => <TextField required variant="standard" {...params} />}
                />
              </FormControl>
            </GridItem>
            <GridItem sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'stretch' }}>
              <Tooltip
                title={
                  disabledFilterByCoverageArea
                    ? i18n('patients.details.serviceRequests.searchService.notFoundCoverageByArea', 'crs')
                    : i18n('patients.details.serviceRequests.searchService.filterCoverageByArea', 'crs')
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={isLoading || disabledFilterByCoverageArea}
                      checked={!!serviceCoverageArea}
                      onClick={() => updateStateFilters({ serviceCoverageArea: !serviceCoverageArea })}
                    />
                  }
                  label={i18n('patients.details.serviceRequests.searchService.filterCoverageByArea', 'crs')}
                />
              </Tooltip>
            </GridItem>
          </GridSection>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={handleSearchServices}>
              {i18n('patients.details.serviceRequests.searchService.search', 'crs')}
            </Button>
          </Box>
        </form>
      </Grid>
      <GridSection sx={{ mt: 0, pt: 0 }}>
        <GridItem xs={12} sx={{ ml: 1, mt: 0, pt: 0 }}>
          <Box display="flex" justifyContent="flex-end" mb={0.5} mt={0}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" />
                {i18n('patients.details.serviceRequests.searchService.listView', 'crs') || 'List'}
              </ToggleButton>
              <ToggleButton value="map" aria-label="map view" disabled={!configurations?.googleMapsApiKey}>
                <MapIcon sx={{ mr: 0.5 }} fontSize="small" />
                {i18n('patients.details.serviceRequests.searchService.mapView', 'crs') || 'Map'}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {viewMode === 'list' ? (
            <Box sx={{ width: '100%', height: 350 }}>
              <DataGrid
              rows={services ?? []}
              sx={{
                width: '100% !important',
                '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' },
                '& .MuiDataGrid-cell': {
                  border: 0,
                },
              }}
              loading={isLoading}
              getRowId={({ healthcareService }) => healthcareService?.id ?? uuidv4()}
              getRowSpacing={() => ({ bottom: 5 })}
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 90}
              columns={columns}
              components={{
                Footer: () => <></>
              }}
              rowSpacingType="margin"
              isRowSelectable={() => false}
              disableColumnFilter
              disableColumnMenu
              density={'compact'}
            />
          </Box>
          ) : configurations?.googleMapsApiKey ? (
            <SearchServicesMapView
              apiKey={configurations.googleMapsApiKey}
              mapCenter={mapCenter}
              patientAddressMarkers={patientAddressMarkers}
              patientName={patient?.getFullName?.() ?? undefined}
              services={services ?? []}
              onSelectService={handleSelectedService}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {i18n('patients.details.serviceRequests.searchService.mapNotConfigured', 'crs') ||
                  'Map is not configured'}
              </Typography>
            </Box>
          )}
        </GridItem>
      </GridSection>
    </>
  );
};

const setRow = (row: any) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Typography variant="subtitle1">{row?.name}</Typography>
      </Box>

      <Box>{row?.comment}</Box>

      {row?.address ? <Box>{getFullAddress(row?.address)}</Box> : null}

      {row?.telecom &&
        row?.telecom?.map((t: any, index: number) => (
          <Box key={'box-telecom-' + index}>{t?.value}</Box>
        ))}
    </Box>
  );
};

const getCodeableConceptDisplay = (
  concept: { text?: string; coding?: { display?: string }[] } | null | undefined
): string => {
  if (!concept) return '';
  if (concept.text) return concept.text;
  return concept.coding?.[0]?.display || '';
};

const setServiceRow = (healthcareService: any) => {
  if (!healthcareService) return null;
  const typeDisplay = getCodeableConceptDisplay(healthcareService.type?.[0]);
  const categoryDisplay = getCodeableConceptDisplay(healthcareService.category?.[0]);
  const typeCategoryParts = [typeDisplay, categoryDisplay].filter(Boolean);
  const typeCategoryText = typeCategoryParts.length > 0 ? typeCategoryParts.join(' / ') : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Typography variant="subtitle1">{healthcareService?.name}</Typography>
      </Box>
      {typeCategoryText ? (
        <Typography variant="caption" color="text.secondary">
          {typeCategoryText}
        </Typography>
      ) : null}
      <Box>{healthcareService?.comment}</Box>
      {healthcareService?.address ? <Box>{getFullAddress(healthcareService?.address)}</Box> : null}
      {healthcareService?.telecom &&
        healthcareService?.telecom?.map((t: any, index: number) => (
          <Box key={'box-telecom-' + index}>{t?.value}</Box>
        ))}
    </Box>
  );
};

export default SearchServices;
